import React from 'react';
import toast from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';

import { TextButton } from '@components/Button/variants';
import IconArrow from '@components/Images/IconArrow';
import SubscriptionForm from '@components/SubscriptionForm';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import useEventTracking from '@hooks/useEventTracking';
import { subscribe_path } from '@routes';
import { Page } from '@tracking';
import { OfferOption } from '@utils/backend-api/subscribe';
import { LeadTypes } from '@utils/enums';
import { FormLocation } from '@utils/enums';
import { extractQueryParams } from '@utils/queryStringHelper';

import CorporateInquiry from './OfferSelection/CorporateInquiry';
import Offer from './OfferSelection/Offer';
import StaticBenefits from './OfferSelection/StaticBenefits';

interface SingleOffersProps {
  description?: string;
  offer: OfferOption;
}

const SingleOffer = ({ description, offer }: SingleOffersProps) => {
  const { code, header, isPro, subHeader } = offer;
  const { Track } = useEventTracking({ page: Page.SingleOfferSubscribe });
  const { source: referringSource } = extractQueryParams(['source']);
  const { article_id, modal } = extractQueryParams(['article_id', 'modal']);
  const { lead_type } = extractQueryParams(['lead_type']);
  const leadType = Object.values(LeadTypes).includes(lead_type as LeadTypes)
    ? (lead_type as LeadTypes)
    : LeadTypes.SubscribePage;
  const {
    user: { dobVerified, isYPEligible },
  } = useSubscribeForm();

  if (offer.isYp && !isYPEligible && dobVerified) {
    toast.success('You are not eligible for the Young Professionals plan.');
  }
  const isMonthly = offer.isMonthly;

  return (
    <Track>
      <div className="px-16 pt-32 sm:px-28 sm:pt-48 lg:pt-64" data-testid="subscribe-single-offer">
        <div className="mx-auto max-w-full lg:max-w-[1040px]">
          {referringSource === 'dual' && (
            <TextButton
              as="link"
              href={subscribe_path({
                ...(article_id && { article_id }),
                ...(modal && { modal }),
              })}
              className="mb-48 text-primary-black no-underline hover:!text-primary-magenta"
            >
              <>
                <IconArrow color="currentColor" className="mr-16 w-[17px]" />
                <span className="font-suisse-intl text-button-sm text-inherit">Back</span>
              </>
            </TextButton>
          )}
          <div className="mb-48 grid grid-cols-1 gap-32 sm:gap-48 md:grid-cols-2 lg:gap-64">
            <div>
              <h2 className="m-0 font-suisse-works text-headline-md text-primary-black sm:text-headline-xl">
                {ReactHtmlParser(header)}
              </h2>
              <p className="mb-0 mt-8 font-suisse-neue text-body-lg text-primary-black sm:mt-16 md:mb-32">
                {description || ReactHtmlParser(subHeader)}
              </p>
              <StaticBenefits className="hidden md:block" isPro={isPro} isMonthly={isMonthly} />
            </div>
            <div className="m-auto w-full xs:max-w-md md:max-w-full">
              <div className="mx-16 mb-32 xs:mx-auto">
                <Offer offer={offer} isSingleOffer selectedOfferCode={code} />
              </div>
              <StaticBenefits
                className="mx-auto block md:hidden"
                isPro={isPro}
                isMonthly={isMonthly}
              />
              <SubscriptionForm
                buttonText="Subscribe"
                formLocation={FormLocation.Subscribe}
                leadType={leadType}
                presentedOfferCodes={[code]}
                selectedOffer={offer}
              />
            </div>
          </div>
          <CorporateInquiry />
        </div>
      </div>
    </Track>
  );
};

export default SingleOffer;
